// Lazy client-side Sentry bootstrap. The DSN itself is safe to expose, but we
// fetch it from a server endpoint so it lives in a runtime secret (not a
// committed VITE_* env), and we can rotate without a redeploy.
//
// The release identifier is baked in at build time (VITE_SENTRY_RELEASE,
// derived in vite.config.ts) so client events match the source maps
// uploaded by @sentry/vite-plugin for the same release.

import { sanitizeAuditData } from "@/lib/security/pii";
import { redactLogMetadata } from "@/lib/security/redact";

let initialized = false;

export async function initSentry(): Promise<void> {
  if (initialized || typeof window === "undefined") return;
  try {
    const res = await fetch("/api/public/sentry-config", { credentials: "omit" });
    if (!res.ok) return;
    const { dsn, release: serverRelease } = (await res.json()) as {
      dsn?: string;
      release?: string;
    };
    if (!dsn) return;
    const release =
      (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || serverRelease || undefined;
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn,
      release,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      beforeSend(event) {
        if (event.user) {
          delete event.user.email;
          delete event.user.ip_address;
        }
        if (event.request?.cookies) delete event.request.cookies;
        if (event.request?.headers) {
          event.request.headers = redactLogMetadata(event.request.headers) as Record<string, string>;
        }
        if (event.extra) event.extra = sanitizeAuditData(event.extra) as Record<string, unknown>;
        if (event.message) event.message = String(redactLogMetadata(event.message));
        return event;
      },
    });
    initialized = true;
  } catch {
    // best-effort: never break the app because telemetry failed
  }
}
